import { useId, useState } from 'react';
import { ActionIcon, Badge, Button, Text } from '@repletoryreach/sdk-ui';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { Popover } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';
import { HelpButton } from '../../../app/guides/HelpButton';
import { AvailabilitySwitch } from './AvailabilitySwitch';

/**
 * Which slice of the queue the agent is looking at.
 *
 * The reference design puts four tabs across the top of the list rather than a
 * queue picker and a separate state dropdown, so the two are merged: three of
 * these are state filters and one is an assignment filter, but from the
 * agent's side they are just the four views of the queue that matter.
 * "Unassigned", which the old picker had, keeps its own home behind the filter
 * button so nothing was taken away.
 */
export type Queue = 'all' | 'open' | 'mine' | 'resolved';

export const QUEUES: Queue[] = ['all', 'open', 'mine', 'resolved'];

export interface QueueFiltersProps {
  queue: Queue;
  onQueue: (q: Queue) => void;
  /** Extra state filter, applied on top of the tab. */
  state: string;
  onState: (s: string) => void;
  /** Show only conversations with nobody assigned. */
  unassignedOnly: boolean;
  onUnassignedOnly: (v: boolean) => void;
  search: string;
  onSearch: (s: string) => void;
  /**
   * The team filter (7 October 2026): '' for any team, 'none' for conversations with no team, else a
   * team's id. Shown only when the organisation has teams.
   */
  team?: string;
  onTeam?: (team: string) => void;
  teams?: { id: string; name: string }[];
  /** Per-tab totals from the server, where they have arrived. */
  counts?: Partial<Record<Queue, number>>;
  /**
   * Start a message to somebody who has not written first. The inbox could
   * only ever reply to a thread that already existed, so the one thing an
   * agent could not do from here was be the one to start.
   */
  onNewMessage?: () => void;
}

export function QueueFilters({
  queue, onQueue, state, onState, unassignedOnly, onUnassignedOnly, search, onSearch, counts, onNewMessage,
  team = '', onTeam, teams = []
}: QueueFiltersProps) {
  const t = useT();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const panelId = useId();
  const showTeams = teams.length > 0 && !!onTeam;
  const extraFilters = !!state || unassignedOnly || (showTeams && !!team);

  return (
    <div className="rr-inbox2__filters">
      <div className="rr-inbox2__filters-head">
        <Text role="body" fw={700} className="rr-inbox2__filters-title">{t('inbox.conversations')}</Text>
        <span className="rr-inbox2__filters-actions">
          <HelpButton guide="inbox" />
          {onNewMessage && (
            <Button size="xs" onClick={onNewMessage} data-guide="inbox-new">{t('inbox.newMessage')}</Button>
          )}
        </span>
      </div>

      <AvailabilitySwitch />

      <div className="rr-inbox2__search-row" data-guide="inbox-search">
        <span className="rr-inbox2__search">
          <span className="rr-inbox2__search-icon" aria-hidden><SearchIcon /></span>
          <TextInput
            size="sm"
            type="search"
            className="rr-inbox2__search-input"
            placeholder={t('inbox.search.placeholder')}
            aria-label={t('inbox.search.label')}
            value={search}
            onChange={(e) => onSearch(e.currentTarget.value)}
          />
        </span>

        {/* The state filter is a second-order control, so it folds away behind
            an icon rather than taking a row of its own on every screen. The
            dot says a filter is active while the panel is shut. */}
        <Popover opened={filtersOpen} onChange={setFiltersOpen} position="bottom-end" width={230}>
          <Popover.Target>
            <ActionIcon
              variant="default"
              size="md"
              aria-label={t('inbox.filters.more')}
              aria-controls={panelId}
              className={`rr-inbox2__filter-btn${extraFilters ? ' is-active' : ''}`}
            >
              <FilterIcon />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <div id={panelId} className="rr-inbox2__filter-panel">
              <Select
                size="xs"
                label={t('inbox.filters.state')}
                value={state}
                onChange={(e) => onState(e.currentTarget.value)}
                data={[
                  { value: '', label: t('inbox.filters.state.any') },
                  { value: 'open', label: t('inbox.state.open') },
                  { value: 'pending', label: t('inbox.state.pending') },
                  { value: 'resolved', label: t('inbox.state.resolved') },
                  { value: 'archived', label: t('inbox.state.archived') }
                ]}
              />
              {showTeams && (
                <Select
                  size="xs"
                  label={t('inbox.filters.team')}
                  value={team}
                  onChange={(e) => onTeam?.(e.currentTarget.value)}
                  data={[
                    { value: '', label: t('inbox.filters.team.any') },
                    { value: 'none', label: t('inbox.filters.team.none') },
                    ...teams.map((x) => ({ value: x.id, label: x.name }))
                  ]}
                />
              )}
              <label className="rr-inbox2__filter-check">
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={(e) => onUnassignedOnly(e.currentTarget.checked)}
                />
                {t('inbox.queue.unassigned')}
              </label>
            </div>
          </Popover.Dropdown>
        </Popover>
      </div>

      <div className="rr-inbox2__tabs" role="tablist" aria-label={t('inbox.filters.queue')} data-guide="inbox-tabs">
        {QUEUES.map((q) => (
          <button
            key={q}
            type="button"
            role="tab"
            aria-selected={queue === q}
            className={`rr-inbox2__tab${queue === q ? ' is-active' : ''}`}
            onClick={() => onQueue(q)}
          >
            {t(`inbox.queue.${q}`)}
            {counts?.[q] !== undefined && (
              <Badge size="xs" variant="light" className="rr-inbox2__tab-count">{counts[q]}</Badge>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.4 10.4 L14 14" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 3.5h12L9.5 8.8V13L6.5 11.5V8.8z" />
    </svg>
  );
}
